import { Localized, useLocalization } from "@fluent/react";
import { Card, CardContent, CardHeader, LinearProgress, Alert, TextField, Typography, Button } from "@mui/material";
import { Box } from "@mui/system";
import { Link } from "react-router-dom";
import LangSwitcher from "../logic/LangSwitcher";

export enum RejectionReason {
    Network,
    Bug,
    Invalid,
    Conflict,
}

interface ScreenProps {
    pending?: boolean,
    rejectionReason?: RejectionReason | null,
    onSubmit?: () => void,
    children?: React.ReactNode
}

function Screen(props: ScreenProps) {
    return <Box sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#EAEEF3',
    }}>
        <Card
            sx={{ maxWidth: '500px', width: '100%' }}
            component="form"
            onSubmit={(e) => { e.preventDefault(); props.onSubmit && props.onSubmit() }}
        >
            {props.pending
                ? <LinearProgress sx={{ height: "8px" }} />
                : <div style={{ height: "8px" }} />
            }

            <Localized id="account-creation-header" attrs={{ title: true, subheader: true }}>
                <CardHeader />
            </Localized>

            {props.rejectionReason &&
                <CardContent>
                    <Error rejectionReason={props.rejectionReason} />
                </CardContent>
            }

            {props.children}

            <CardContent sx={{ borderTop: '1px #c4c4c4 solid', display: 'flex', justifyContent: 'center' }}>
                <LangSwitcher />
            </CardContent>
        </Card>
    </Box>;
}

export enum ConfirmPasswordErrors {
    Mismatch,
}

export interface Field<E = undefined> {
    value: string;
    setValue(value: string): void;
    error?: E
}

export interface ErrorScreenProps {
    rejectionReason: RejectionReason;
}

export function ErrorScreen(props: ErrorScreenProps) {
    return <Screen
        rejectionReason={props.rejectionReason}
        />;
}

export interface InputScreenProps {
    pending: boolean;
    rejectionReason: RejectionReason | null;
    passwordStrength: 0 | 1 | 2 | 3 | 4;

    givenName: Field;
    familyName: Field;
    password: Field;
    confirmPassword: Field<ConfirmPasswordErrors>;

    onSubmit?: () => void,
}

export function InputScreen(props: InputScreenProps) {
    const passwordManagerLink = useLocalization().l10n.getString("password-manager-link");

    return <Screen
        pending={props.pending}
        rejectionReason={props.rejectionReason}
        onSubmit={props.onSubmit}
    >
        <CardContent>
            <Box>
                <Box sx={{ display: "flex" }}>
                    <Localized id="account-creation-given-name" attrs={{ label: true }}>
                        <TextField
                            value={props.givenName.value}
                            onChange={e => props.givenName.setValue(e.target.value)}
                            disabled={props.pending}
                            variant="outlined"
                            margin="normal"
                            required
                            sx={{ flexGrow: 1, marginRight: '16px' }}
                        />
                    </Localized>
                    <Localized id="account-creation-family-name" attrs={{ label: true }}>
                        <TextField
                            value={props.familyName.value}
                            onChange={e => props.familyName.setValue(e.target.value)}
                            disabled={props.pending}
                            variant="outlined"
                            margin="normal"
                            required
                            sx={{ flexGrow: 1 }}
                        />
                    </Localized>
                </Box>

                {/* <Localized id="account-creation-name"
                    vars={{ prefilled: props.hasPrefilledName ? 'yes' : 'no' }}
                >
                    <Typography />
                </Localized> */}


                <Localized id="account-creation-password" attrs={{ label: true }}>
                    <TextField
                        type="password"
                        value={props.password.value}
                        onChange={e => props.password.setValue(e.target.value)}
                        disabled={props.pending}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth />
                </Localized>
                <Localized
                    id="account-creation-password-description"
                    elems={{
                        // Eslint complains that this link has no context,
                        // but the content is being dynamically loaded in by
                        // Fluent.
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        'pw-link': <a href={passwordManagerLink}></a>
                    }}
                >
                    <Typography />
                </Localized>
                <Localized id="account-creation-confirm-password"
                    attrs={{
                        label: true,
                        helperText: props.confirmPassword.error === ConfirmPasswordErrors.Mismatch
                    }}>
                    <TextField
                        type="password"
                        value={props.confirmPassword.value}
                        onChange={e => props.confirmPassword.setValue(e.target.value)}
                        disabled={props.pending}
                        variant="outlined"
                        margin="normal"
                        required
                        error={props.confirmPassword.error === ConfirmPasswordErrors.Mismatch}
                        fullWidth />
                </Localized>
                <Localized id="account-creation-confirm-password-description">
                    <Typography />
                </Localized>

            </Box>
        </CardContent>

        <CardContent sx={{ borderTop: '1px #c4c4c4 solid' }}>
            {props.password.value && <Box sx={{ marginBottom: '16px' }}>
                <Localized id="account-creation-password-strength">
                    <Typography variant="h5" />
                </Localized>
                <LinearProgress
                    variant="determinate"
                    value={(props.passwordStrength + 1) / 5 * 100}
                    color={
                        props.passwordStrength === 0 ? 'error' :
                        props.passwordStrength === 1 ? 'error' :
                        props.passwordStrength === 2 ? 'warning' :
                        props.passwordStrength === 3 ? 'success' :
                        props.passwordStrength === 4 ? 'success' :
                        undefined
                    }
                    sx={{ height: "8px" }}
                />
                {props.passwordStrength <= 2 &&
                    <Localized id="account-creation-password-too-weak">
                        <Typography variant="subtitle2" />
                    </Localized>
                }
            </Box>}

            <Localized id="account-creation-submit-button">
                <Button
                    sx={{
                        margin: '0 auto',
                        display: 'block'
                    }}
                    type="submit"
                    disabled={props.pending || !props.onSubmit}
                    variant="contained"
                />
            </Localized>
        </CardContent>

    </Screen>;
}

interface ErrorProps {
    rejectionReason: RejectionReason;
}

function rejectionReasonMsg(rejectionReason: RejectionReason): string {
	switch (rejectionReason) {
		case RejectionReason.Bug:
            return "account-creation-feedback-bug"
		case RejectionReason.Network:
            return "account-creation-feedback-network"
        case RejectionReason.Invalid:
            return "account-creation-feedback-invalid"
        case RejectionReason.Conflict:
            return "account-creation-feedback-conflict"
	}
}

function Error(props: ErrorProps) {
    return (
        <Localized id={rejectionReasonMsg(props.rejectionReason)}>
            <Alert severity="error" />
        </Localized>
    )
}

export function DoneScreen() {
    return <Screen pending={false}>
        <CardContent>
            <Localized id="account-creation-feedback-success">
                <Alert severity="success" />
            </Localized>
            <Localized id="account-creation-continue-button">
                <Button
                    variant="contained"
                    sx={{
                        margin: '16px auto 0px',
                        display: 'block',
                        width: 'fit-content',
                    }}
                    component={Link}
                    to="/"
                />
            </Localized>
        </CardContent>
    </Screen>;
}
